import { useState, useEffect } from 'react';

interface ScheduleProps {
  userProfile: any;
}

export default function Schedule({ userProfile }: ScheduleProps) {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [schedule, setSchedule] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showStudentList, setShowStudentList] = useState(false);
  const [students, setStudents] = useState<any[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);

  const daysOfWeek = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'Chủ nhật'];
  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', 
    '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'
  ];

  useEffect(() => {
    fetchSchedule();
  }, [currentWeek, userProfile]);

  const parseSchedule = (scheduleText: string) => {
    // Parse schedule text like "Thứ 2, 4, 6 (18:00 - 20:00)"
    const scheduleItems: any[] = [];
    
    if (!scheduleText) return scheduleItems;

    try {
      // Extract days - chỉ lấy các số sau "Thứ" và trước dấu ngoặc
      const beforeParenthesis = scheduleText.split('(')[0]; // Lấy phần trước dấu ngoặc
      const daysMatch = beforeParenthesis.match(/Thứ\s*([0-9,\s]+)/i);
      const timeMatch = scheduleText.match(/\(?\s*(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})\s*\)?/);
      
      if (daysMatch && timeMatch) {
        // Tách các số và loại bỏ khoảng trắng
        const days = daysMatch[1]
          .split(',')
          .map(d => d.trim())
          .filter(d => d !== '')
          .map(d => parseInt(d));
        
        const startTime = timeMatch[1];
        const endTime = timeMatch[2];
        
        console.log('Parsed days:', days);
        console.log('Parsed time:', startTime, '-', endTime);
        
        days.forEach(day => {
          scheduleItems.push({
            day: day,
            start_time: startTime,
            end_time: endTime
          });
        });
      }
    } catch (error) {
      console.error('Error parsing schedule:', error);
    }

    return scheduleItems;
  };

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      // Get teacher's courses
      const coursesResponse = await fetch(`${supabaseUrl}/functions/v1/get-courses`, {
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!coursesResponse.ok) {
        throw new Error('Failed to fetch courses');
      }

      const allCourses = await coursesResponse.json();
      console.log('All courses:', allCourses);

      // Filter courses by teacher
      const teacherCourses = allCourses.filter((course: any) => 
        course.teacher_id === userProfile.id
      );
      console.log('Teacher courses:', teacherCourses);

      // Get enrollments for each course
      const scheduleData: any[] = [];
      const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-indigo-500'];
      
      for (let i = 0; i < teacherCourses.length; i++) {
        const course = teacherCourses[i];
        
        // Get student count
        const enrollmentsResponse = await fetch(`${supabaseUrl}/functions/v1/get-enrollments`, {
          headers: {
            'Authorization': `Bearer ${supabaseKey}`,
            'Content-Type': 'application/json'
          }
        });

        let studentCount = 0;
        if (enrollmentsResponse.ok) {
          const enrollments = await enrollmentsResponse.json();
          studentCount = enrollments.filter((e: any) => e.course_id === course.id).length;
        }

        // Parse schedule
        const scheduleItems = parseSchedule(course.schedule || '');
        console.log('Schedule items for', course.title, ':', scheduleItems);

        scheduleItems.forEach((item, index) => {
          scheduleData.push({
            id: `${course.id}_${index}`,
            course_id: course.id,
            course: course.title,
            day: item.day,
            start_time: item.start_time,
            end_time: item.end_time,
            room: 'Phòng học',
            students_count: studentCount,
            color: colors[i % colors.length],
            type: 'regular'
          });
        });
      }

      console.log('Final schedule data:', scheduleData);
      setSchedule(scheduleData);
    } catch (error) {
      console.error('Error fetching schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async (courseId: string) => {
    setLoadingStudents(true);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      // Get enrollments for this course
      const enrollmentsResponse = await fetch(`${supabaseUrl}/functions/v1/get-enrollments`, {
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!enrollmentsResponse.ok) {
        throw new Error('Failed to fetch enrollments');
      }

      const allEnrollments = await enrollmentsResponse.json();
      const courseEnrollments = allEnrollments.filter((e: any) => e.course_id === courseId);

      // Get user details for each enrollment
      const usersResponse = await fetch(`${supabaseUrl}/functions/v1/get-users`, {
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!usersResponse.ok) {
        throw new Error('Failed to fetch users');
      }

      const allUsers = await usersResponse.json();
      
      // Match enrollments with user details
      const studentList = courseEnrollments.map((enrollment: any) => {
        const user = allUsers.find((u: any) => u.id === enrollment.student_id);
        return {
          ...enrollment,
          student_name: user?.full_name || 'Chưa cập nhật',
          student_email: user?.email || '',
          student_phone: user?.phone || ''
        };
      });

      setStudents(studentList);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleViewStudents = () => {
    if (selectedClass) {
      setShowModal(false);
      fetchStudents(selectedClass.course_id);
      setShowStudentList(true);
    }
  };

  const handleCreateAssignment = () => {
    if (selectedClass) {
      setShowModal(false);
      setShowAssignmentForm(true);
    }
  };

  const getWeekDates = (date: Date) => {
    const week = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const weekDates = getWeekDates(currentWeek);

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentWeek);
    newDate.setDate(currentWeek.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeek(newDate);
  };

  const getClassesForTimeSlot = (day: number, time: string) => {
    // day parameter là index từ 0-6 (0=Thứ 2, 1=Thứ 3, ..., 6=Chủ nhật)
    // cls.day là số thứ trong tuần (2=Thứ 2, 3=Thứ 3, ..., 7=Thứ 7, 8=Chủ nhật)
    // Cần chuyển đổi: index 0 -> day 2, index 1 -> day 3, ..., index 5 -> day 7, index 6 -> day 8
    const dayNumber = day === 6 ? 8 : day + 2; // Chủ nhật = 8, các ngày khác = index + 2
    
    return schedule.filter(cls => {
      const startHour = parseInt(cls.start_time.split(':')[0]);
      const currentHour = parseInt(time.split(':')[0]);
      const endHour = parseInt(cls.end_time.split(':')[0]);
      
      return cls.day === dayNumber && currentHour >= startHour && currentHour < endHour;
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatTime = (time: string) => {
    return time; // Trả về trực tiếp định dạng 24h
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-gray-500">
          <i className="ri-loader-4-line animate-spin text-xl"></i>
          <span>Đang tải lịch dạy...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Lịch dạy</h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigateWeek('prev')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </button>
          <div className="text-lg font-medium text-gray-900">
            {weekDates[0].toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })} - {' '}
            {weekDates[6].toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </div>
          <button
            onClick={() => navigateWeek('next')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>
      </div>

      {/* Schedule Grid */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Header */}
            <div className="grid grid-cols-8 border-b bg-gray-50">
              <div className="p-4 text-sm font-medium text-gray-600">Giờ</div>
              {daysOfWeek.map((day, index) => (
                <div key={day} className="p-4 text-center">
                  <div className="text-sm font-medium text-gray-900">{day}</div>
                  <div className={`text-xs mt-1 ${isToday(weekDates[index]) ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                    {weekDates[index].getDate()}/{weekDates[index].getMonth() + 1}
                  </div>
                </div>
              ))}
            </div>

            {/* Time slots */}
            {timeSlots.map((time) => (
              <div key={time} className="grid grid-cols-8 border-b hover:bg-gray-50">
                <div className="p-4 text-sm text-gray-600 border-r bg-gray-50">
                  {time}
                </div>
                {daysOfWeek.map((_, dayIndex) => {
                  const classes = getClassesForTimeSlot(dayIndex, time);
                  return (
                    <div key={dayIndex} className="p-2 border-r min-h-[60px] relative">
                      {classes.map((cls) => (
                        <div
                          key={cls.id}
                          onClick={() => {
                            setSelectedClass(cls);
                            setShowModal(true);
                          }}
                          className={`${cls.color} text-white p-2 rounded text-xs cursor-pointer hover:opacity-90 transition-opacity mb-1`}
                        >
                          <div className="font-medium truncate">{cls.course}</div>
                          <div className="opacity-90">{cls.room}</div>
                          <div className="opacity-90">{cls.students_count} HS</div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Today's Classes Summary */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Lịch dạy hôm nay</h2>
        <div className="space-y-3">
          {schedule
            .filter(cls => {
              const today = new Date().getDay();
              const todayAdjusted = today === 0 ? 8 : today + 1; // Chủ nhật = 8, Thứ 2 = 2, Thứ 3 = 3, ...
              return cls.day === todayAdjusted;
            })
            .sort((a, b) => a.start_time.localeCompare(b.start_time))
            .map((cls) => (
              <div key={cls.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className={`w-4 h-4 ${cls.color} rounded`}></div>
                  <div>
                    <div className="font-medium text-gray-900">{cls.course}</div>
                    <div className="text-sm text-gray-600">
                      {cls.start_time} - {cls.end_time} • {cls.room} • {cls.students_count} học sinh
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setSelectedClass(cls);
                    setShowModal(true);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm cursor-pointer whitespace-nowrap"
                >
                  Chi tiết
                </button>
              </div>
            ))}
          {schedule.filter(cls => {
            const today = new Date().getDay();
            const todayAdjusted = today === 0 ? 8 : today + 1;
            return cls.day === todayAdjusted;
          }).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <i className="ri-calendar-line text-3xl mb-2"></i>
              <p>Hôm nay bạn không có lịch dạy</p>
            </div>
          )}
        </div>
      </div>

      {/* Class Detail Modal */}
      {showModal && selectedClass && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Chi tiết lớp học</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{selectedClass.course}</h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <i className="ri-time-line mr-2"></i>
                      {selectedClass.start_time} - {selectedClass.end_time}
                    </div>
                    <div className="flex items-center">
                      <i className="ri-map-pin-line mr-2"></i>
                      {selectedClass.room}
                    </div>
                    <div className="flex items-center">
                      <i className="ri-group-line mr-2"></i>
                      {selectedClass.students_count} học sinh
                    </div>
                  </div>
                </div>

                <div className="space-y-3 pt-4">
                  <button 
                    onClick={handleViewStudents}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-group-line mr-2"></i>
                    Xem danh sách học sinh
                  </button>
                  <button 
                    onClick={handleCreateAssignment}
                    className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-file-list-line mr-2"></i>
                    Giao bài tập
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Student List Modal */}
      {showStudentList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Danh sách học sinh</h2>
                <button
                  onClick={() => setShowStudentList(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
              {loadingStudents ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center space-x-2 text-gray-500">
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    <span>Đang tải danh sách...</span>
                  </div>
                </div>
              ) : students.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <i className="ri-user-line text-4xl mb-2"></i>
                  <p>Chưa có học sinh đăng ký</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {students.map((student, index) => (
                    <div key={student.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-medium">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{student.student_name}</div>
                          <div className="text-sm text-gray-600">{student.student_email}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(student.enrolled_at).toLocaleDateString('vi-VN')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assignment Form Modal */}
      {showAssignmentForm && selectedClass && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Giao bài tập mới</h2>
                <button
                  onClick={() => setShowAssignmentForm(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Khóa học
                  </label>
                  <input
                    type="text"
                    value={selectedClass.course}
                    disabled
                    className="w-full px-4 py-2 border rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tiêu đề bài tập <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Nhập tiêu đề bài tập"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mô tả <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Nhập mô tả chi tiết bài tập"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hạn nộp <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Điểm tối đa
                    </label>
                    <input
                      type="number"
                      placeholder="100"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAssignmentForm(false)}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Giao bài tập
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
